import { ensure, Optional } from "sprint-common-web";

import { MovieVersionDocumentResponse } from "../../../../common/api/catalogue-api/movie/MovieDocumentResponse";
import { EpisodeVersionDocumentResponse } from "../../../../common/api/catalogue-api/tv-api/TvSeriesDocumentResponse";
import { useContentApi } from "../../../../common/data/data-hooks/content/useContentApi";
import {
    ContentReference,
    ContentMovieVersion,
    ContentEpisodeVersion,
    ContentMovieReference,
    ContentTvSeriesReference,
    ContentAncillaryReference
} from "../../../../common/types/ContentReference";
import { ContentReferenceType } from "../../../../common/types/ContentReferenceType";
import { ContentSubtype } from "../../../../common/types/ContentSubtype";
import { ContentAssociationFormState } from "../types/ContentAssociationFormState";

export const useAssociateFormContentReferenceMapper = () => {
    const { getMovieById, getTvSeriesById, getAncillaryById } = useContentApi();

    const mapFormStateToContentReference = async <T extends ContentAssociationFormState>(
        formState: T
    ): Promise<Optional<ContentReference>> => {
        const { contentType, contentId, versionIds, seasonId, episodeId, airlineId } = formState;

        if (!contentId) return;

        switch (contentType) {
            case ContentReferenceType.Movie:
                return mapToMovieReference(contentId, versionIds);

            case ContentReferenceType.TvSeries:
                return mapToTvSeriesReference(contentId, seasonId, episodeId, versionIds);

            case ContentReferenceType.Ancillary:
                return mapToAncillaryReference(contentId, airlineId);

            default:
                return;
        }
    };

    const mapToMovieReference = async (movieId: string, versionIds?: string[]): Promise<ContentMovieReference> => {
        const movie = await getMovieById(movieId);

        const versions: ContentMovieVersion[] = getMovieOrEpisodeVersions(movie.versions, versionIds);

        return {
            type: ContentReferenceType.Movie,
            movie: {
                id: movie.id,
                title: movie.title,
                buzzId: movie.buzz?.id
            },
            subtype: movie.subtype,
            versions
        };
    };

    const mapToTvSeriesReference = async (
        tvSeriesId: string,
        seasonId?: string,
        episodeId?: string,
        versionIds?: string[]
    ): Promise<ContentTvSeriesReference> => {
        const tvSeries = await getTvSeriesById(tvSeriesId);

        const season = tvSeries.seasons.find((s) => s.id === seasonId);

        const episode = season?.episodes.find((s) => s.id === episodeId);

        const episodeVersions: ContentEpisodeVersion[] = episode?.versions
            ? getMovieOrEpisodeVersions(episode.versions, versionIds)
            : [];

        return {
            type: ContentReferenceType.TvSeries,
            episode: episode
                ? {
                      id: episode.id,
                      number: episode.number,
                      title: episode.title,
                      buzzId: episode.buzz?.id
                  }
                : undefined,
            season: season
                ? {
                      id: season.id,
                      name: season.name,
                      buzzId: season.buzzId
                  }
                : undefined,
            subtype: ContentSubtype.TV,
            tvSeries: {
                id: tvSeries.id,
                title: tvSeries.title,
                buzzId: tvSeries.buzzId
            },
            versions: episodeVersions
        };
    };

    const mapToAncillaryReference = async (
        ancillaryId: string,
        airlineId?: string
    ): Promise<ContentAncillaryReference> => {
        const ancillary = await getAncillaryById(ancillaryId);

        const airline = ancillary.version.airlines.find((a) => a.id === airlineId);
        ensure(airline);

        return {
            type: ContentReferenceType.Ancillary,
            airline,
            ancillary: {
                id: ancillary.id,
                title: ancillary.title,
                version: ancillary.version,
                buzzId: ancillary.buzz?.id
            },
            subtype: ancillary.subtype
        };
    };

    return { mapFormStateToContentReference };
};

const getMovieOrEpisodeVersions = (
    versions: (MovieVersionDocumentResponse | EpisodeVersionDocumentResponse)[],
    versionIds?: string[]
) => {
    return versions
        .filter((v) => versionIds?.includes(v.id))
        .map((v) => {
            return {
                id: v.id,
                distributor: v.distributor,
                buzzId: v.buzzVersion?.id,
                type: v.type,
                brandings: v.brandings
            };
        });
};
